import { useRef, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/zh/home';

interface VideoItem {
  id: string;
  title: string;
  cover: string;
  tag: string;
  views: string;
  updateTime: string;
  category: string;
  slug: string;
  excerpt: string;
  author: string;
  publishedAt: string;
  content: string;
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const catName = category?.name || '分类';
  const catDesc = category?.description || '';
  const catImage = category?.image || '';
  const catCount = category?.count || 0;

  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    import('@/mocks/zh/pages').then((mod) => {
      const data = mod.categoryVideos as Record<string, VideoItem[]>;
      setVideos(data[slug || ''] || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !videos.length) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, [videos]);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -340 : 340, behavior: 'smooth' });
  };

  const categoryJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName} - 吃瓜视频`,
    description: catDesc,
    url: `https://nexkinn.cn/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '吃瓜视频',
      url: 'https://nexkinn.cn',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <SEO
        title={`${catName} - 吃瓜视频 网红黑料实时追踪`}
        description={`吃瓜视频${catName}分类，${catDesc}，每日更新${catCount}条相关内容，国产吃瓜视频在线观看，网红塌房现场实时追踪。`}
        keywords={`${catName}, 吃瓜视频在线, 国产吃瓜视频, 吃瓜视频网站, 吃瓜视频在线观看`}
        canonicalUrl={`/category/${slug}`}
        jsonLd={categoryJsonLd}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: catName }]} />

        <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
          <img
            src={catImage}
            alt={`${catName} - ${catDesc}`}
            title={`${catName}吃瓜视频 - ${catDesc}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              {catName}
            </h1>
            <p className="text-sm md:text-base text-white/70 max-w-lg">
              {catDesc}
            </p>
            <span className="mt-3 text-xs text-white/50 bg-white/10 px-3 py-1 rounded-full">
              共 {catCount.toLocaleString()} 条内容
            </span>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              最新{catName}
            </h2>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i>
              </div>
            ) : videos.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-inbox-line text-4xl text-foreground-300 mb-4 block"></i>
                <p className="text-foreground-500">暂未找到相关内容</p>
              </div>
            ) : (
              <div className="relative">
                {canScrollLeft && (
                  <button
                    onClick={() => scroll('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-background-300 text-foreground-700 hover:text-foreground-950 transition-all cursor-pointer"
                    aria-label="向左滚动"
                  >
                    <i className="ri-arrow-left-s-line text-lg"></i>
                  </button>
                )}
                {canScrollRight && (
                  <button
                    onClick={() => scroll('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-background-50 border border-background-300 text-foreground-700 hover:text-foreground-950 transition-all cursor-pointer"
                    aria-label="向右滚动"
                  >
                    <i className="ri-arrow-right-s-line text-lg"></i>
                  </button>
                )}
                <div
                  ref={scrollRef}
                  className="flex gap-5 overflow-x-auto pb-4"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {videos.map((video, idx) => (
                    <Link
                      key={video.id}
                      to={`/article/${video.slug}`}
                      className="flex-shrink-0 w-[280px] md:w-[320px] group cursor-pointer"
                    >
                      <div className="relative rounded-xl overflow-hidden bg-background-100">
                        <div className="aspect-[4/3] relative overflow-hidden">
                          <img
                            src={video.cover}
                            alt={video.title}
                            title={`${video.title} - 吃瓜视频`}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                            loading={idx < 2 ? 'eager' : 'lazy'}
                          />
                          {video.tag && (
                            <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-primary-500/90 text-white text-xs font-medium whitespace-nowrap">
                              {video.tag}
                            </span>
                          )}
                          <div className="absolute bottom-3 right-3 px-2 py-0.5 rounded bg-black/60 text-white text-xs whitespace-nowrap">
                            {video.views} 次播放
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 px-1">
                        <h3 className="text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                          {video.title}
                        </h3>
                        <div className="flex items-center justify-between mt-2 text-xs text-foreground-500">
                          <span>{video.author}</span>
                          <span>{video.updateTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-10">
              热门子话题
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.filter((c) => c.slug !== slug).slice(0, 3).map((c) => (
                <Link
                  key={c.id}
                  to={`/category/${c.slug}`}
                  className="group relative rounded-xl overflow-hidden aspect-[4/3] cursor-pointer"
                >
                  <img
                    src={c.image}
                    alt={c.name}
                    title={`${c.name}吃瓜视频 - ${c.description}`}
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm mb-3">
                      <i className={`${c.icon} text-white text-xl`}></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-white">{c.name}</h3>
                    <p className="text-sm text-white/70 mt-1">{c.description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}